import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm, SubmissionError } from 'redux-form';
import { Helmet } from 'react-helmet';
import { Grid } from '@saucelabs/sl-component-grid';
import { Tabs, Tab } from '@saucelabs/sl-component-tabs';
import { recordTrack as recordTrackAction } from '@saucelabs/sl-util-segmentio';
import validate from '@saucelabs/sl-util-validate';
import { selectors as concurrencySelectors } from '@saucelabs/sl-module-concurrency';
import { Loader } from '@saucelabs/sl-component-loader';

import * as actions from '../../actions';
import * as utils from '../../utils';
import configuratorSelector from '../../selectors';
import { FORM_TAB_STATES, FORM_TYPES } from '../../constants';
import Styles from './AppTestingForm.styles';
import AppSelectionContainer from './AppSelectionContainer';
import ConfigsContainer from './ConfigsContainer';
import RealDevicesContainer from './RealDevicesContainer';

const constraints = {
  [FORM_TYPES.MOBILE_APP]: {
    mobileAppConfiguration: {
      presence: { allowEmpty: false },
    },
    deviceConfiguration: {
      presence: { allowEmpty: false },
    },
  },
};

const getFormErrors = (formValues) => validate(formValues, constraints[FORM_TYPES.MOBILE_APP]);

class AppTestingForm extends Component {
  componentDidMount() {
    const { recordTrack } = this.props;

    recordTrack('Manual Testing Form Launched');
  }

  componentDidUpdate() {
    this.checkIfCurrentTunnelIsStillRunning();
  }

  checkIfCurrentTunnelIsStillRunning = () => {
    const { change, formValues, tunnels } = this.props;

    if (!formValues.tunnel) {
      return;
    }

    if (!utils.isTunnelStillRunning(formValues.tunnel, tunnels)) {
      change('tunnel', null);
    }
  }

  validateDevice = (device) => {
    const {
      formValues,
      sauceConnectPublicRdcEnabled,
      showSauceConnectPublicRdcDisabledNotification,
    } = this.props;
    const publicDevice = !device.isPrivate();
    const sauceConnectRequested = !!formValues.tunnelIdentifier;
    const sauceConnectPublicDevicesDisabled = !sauceConnectPublicRdcEnabled;

    if (publicDevice && sauceConnectRequested && sauceConnectPublicDevicesDisabled) {
      showSauceConnectPublicRdcDisabledNotification();
      return false;
    }

    return true;
  }

  handleFormSubmit = (values) => {
    const { onSubmit } = this.props;
    const errors = getFormErrors(values);

    if (errors) {
      throw new SubmissionError(errors);
    }

    onSubmit(values);
  }

  renderDeviceConfigurationField() {
    const { change, formValues, userRdcId } = this.props;

    const { mobileAppConfiguration } = formValues;
    const platform = mobileAppConfiguration && mobileAppConfiguration.platform;

    return (
      <Loader
        fontSize={3}
        show={!userRdcId}
      >
        <Field
          component={RealDevicesContainer}
          deviceConfiguration={formValues.deviceConfiguration}
          name="deviceConfiguration"
          onDeviceSelect={(device) => {
            const valid = this.validateDevice(device);
            if (valid) {
              change('deviceConfiguration', device);
            }
          }}
          onTunnelChange={(tunnel) => change('tunnelIdentifier', tunnel)}
          os={platform}
        />
      </Loader>
    );
  }

  render() {
    const {
      change,
      clearSubmitErrors,
      dispatch,
      formValues,
      handleSubmit,
      initialize,
      recentConfigs,
      savedConfigs,
      userConcurrency,
    } = this.props;
    const currentAllocated = userConcurrency.get('current').overall;
    const allowedToAllocate = userConcurrency.get('allowed').overall;
    const cannotStartSession = [currentAllocated, allowedToAllocate].every(Number.isFinite) &&
      currentAllocated >= allowedToAllocate;
    const submitDisabled = !!getFormErrors(formValues) || cannotStartSession;

    return (
      <form
        className={Styles.default}
        onSubmit={handleSubmit(this.handleFormSubmit)}
      >
        <Helmet>
          <title>Live App Testing</title>
        </Helmet>
        <Grid
          className={Styles['main-container']}
          fluid
          tagName="section"
        >
          <Field
            component={AppSelectionContainer}
            name="mobileAppConfiguration"
            onAppSelect={(app) => {
              change('mobileAppConfiguration', app);
              change('deviceConfiguration', {});
              return dispatch(clearSubmitErrors());
            }}
          />
          {formValues.mobileAppConfiguration &&
            (
              <Tabs
                animation={false}
                className={Styles['tabs-container']}
                defaultActiveKey={1}
                id="app-testing-tabs"
                mountOnEnter
              >
                <Tab
                  eventKey={1}
                  mountOnEnter
                  title="Real Devices"
                >
                  {this.renderDeviceConfigurationField()}
                </Tab>
              </Tabs>
            )
          }
        </Grid>
        {formValues.mobileAppConfiguration && (
          <Field
            component={ConfigsContainer}
            name="saveConfiguration"
            onItemClick={(config) => {
              initialize({
                ...formValues,
                ...utils.getLiveTestingConfig(config),
              });
            }}
            onToggleSaveConfig={(value) => {
              change('saveConfiguration', value);
            }}
            recentConfigs={recentConfigs}
            savedConfigs={savedConfigs}
            submitDisabled={submitDisabled}
          />
        )}
      </form>
    );
  }
}

AppTestingForm.propTypes = {
  change: PropTypes.func.isRequired,
  clearSubmitErrors: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  formValues: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  recentConfigs: PropTypes.array.isRequired,
  recordTrack: PropTypes.func.isRequired,
  sauceConnectPublicRdcEnabled: PropTypes.bool.isRequired,
  savedConfigs: PropTypes.array.isRequired,
  showSauceConnectPublicRdcDisabledNotification: PropTypes.func.isRequired,
  tunnels: PropTypes.array.isRequired,
  userConcurrency: ImmutablePropTypes.map.isRequired,
  userRdcId: PropTypes.string,
};

AppTestingForm.defaultProps = {
  userRdcId: null,
};

/* we need to have an access to current values in form to handle Tabs state */
const mapStateToProps = (state) => {
  const lastConfig = configuratorSelector(state).lastConfigApp({});

  const isLastConfigTunnelRunning = utils.isTunnelStillRunning(
    lastConfig.tunnel,
    configuratorSelector(state).tunnels,
  );

  return ({
    recentConfigs: configuratorSelector(state).recentConfigsApp,
    savedConfigs: configuratorSelector(state).savedConfigsApp,
    sauceConnectPublicRdcEnabled: configuratorSelector(state).sauceConnectPublicRdcEnabled,
    userConcurrency: concurrencySelectors.concurrencySelector()(state).concurrency,
    tunnels: configuratorSelector(state).tunnels,
    formValues: formValueSelector('manualAppTesting')(
      state,
      'platformType',
      'device',
      'deviceType',
      'deviceConfiguration',
      'mobileAppConfiguration',
      'tunnel',
      'tunnelIdentifier',
    ),
    initialValues: {
      assets: true,
      ...lastConfig,
      device: FORM_TAB_STATES.DEVICE.MOBILE,
      deviceType: FORM_TAB_STATES.DEVICE_TYPE.REAL,
      // this is temporarily added to frontend to remove default app selection, and
      // user should see app table page instead of device selection for the first time
      // after logging in. A ticket is created for making changes in
      // the backend https://saucedev.atlassian.net/browse/RDCWEB-3832
      // Then the line below will be removed from the frontend and it will work out of the box
      // change('mobileAppConfiguration', null);
      mobileAppConfiguration: null,
      platformType: FORM_TAB_STATES.PLATFORM_TYPE.MOBILE,
      tunnel: isLastConfigTunnelRunning ? lastConfig.tunnel : null,
    },
    userRdcId: configuratorSelector(state).userRdcId,
  });
};

const mapDispatchToProps = {
  onSubmit: actions.handleTabsFormSubmit,
  showSauceConnectPublicRdcDisabledNotification: actions.showSauceConnectRdcPublicDisabled,
  recordTrack: recordTrackAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(reduxForm({
  form: 'manualAppTesting',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  updateUnregisteredFields: true,
  touchOnChange: true,
})(AppTestingForm));
export { AppTestingForm };
